/**
 * Touch-target tokens for `@media (pointer: coarse)`.
 *
 * Spec `adaptive-input-ui` requires every interactive control to present a
 * hit area of at least 44 × 44 CSS px on coarse pointers (WCAG 2.5.5 / Apple
 * HIG). The tokens below are the single source of truth; every shadcn/ui
 * primitive that receives tap/click should compose them into its className.
 *
 * Tailwind sizing convention: `h-N` / `size-N` ⇒ `N * 0.25rem`. At the
 * default 16 px root, N = 11 ⇒ 44 px. Do NOT hard-code other sizes without
 * updating the `resolveCoarsePx` test that enforces the invariant.
 */

export const COARSE_MIN_PX = 44;

export const COARSE_TEXT_HEIGHT = "pointer-coarse:h-11";
export const COARSE_TEXT_HEIGHT_LG = "pointer-coarse:h-12";
export const COARSE_ICON_SIZE = "pointer-coarse:size-11";
export const COARSE_ICON_SIZE_LG = "pointer-coarse:size-12";

const REM_TO_PX = 16;
const TAILWIND_UNIT_REM = 0.25;
const UNIT_RE = /(?:h-|size-)(\d+(?:\.\d+)?)/g;

/**
 * Parse the largest `h-N` or `size-N` utility in a Tailwind class string and
 * return its effective pixel value at the default 16 px root. Returns 0 when
 * no matching utility is present. Used by tests to verify that coarse-pointer
 * tokens satisfy `COARSE_MIN_PX` without running a real browser layout pass.
 */
export function resolveCoarsePx(classString: string): number {
  let maxPx = 0;
  for (const match of classString.matchAll(UNIT_RE)) {
    const n = parseFloat(match[1]);
    const px = n * TAILWIND_UNIT_REM * REM_TO_PX;
    if (px > maxPx) {
      maxPx = px;
    }
  }
  return maxPx;
}
